import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Status_Enum,
  type GetMyItemsDashboardQuery,
  Issue_Assessment_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isActionStatusOverdue } from '@/components/action-status-badge/utils';
import { useChangeRequests } from '@/hooks/useChangeRequests';
import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';

import { useGetTypes } from './config';

export type RibbonItem = {
  Id: string;
  Type: string;
  StatusLabelled: string;
};

/**
 * Flattens all entity arrays from the My Items dashboard query into a single
 * RibbonItem list, resolving StatusLabelled for each entity type from the
 * appropriate rating scale or common lookup.
 *
 */
export const useFlattenRibbonData = (
  data: GetMyItemsDashboardQuery | undefined
): RibbonItem[] | undefined => {
  const { t } = useTranslation('common');
  const { isActiveApprover } = useChangeRequests();
  const { getByValue: getCommonLookupByValue } = useCommonLookupLazy();
  const actionRatings = useRating('action_status');
  const assessmentRatings = useRating('assessment_status');
  const activityRatings = useRating('assessment_activity_status');
  const issueRatings = useRating('issue_assessment_status');
  const types = useGetTypes();

  return useMemo(() => {
    // Undefined = the dashboard query hasn't resolved (or failed); the ribbon
    // treats it as not-ready and stays hidden, instead of rendering a
    // loaded-empty state of false zeros (RSP-6539). [] = genuinely no items.
    if (!data) {
      return undefined;
    }

    // Empty StatusLabelled for entities that don't surface a lifecycle status
    // in the My Items view (Controls, Documents, Indicators, Obligations).
    // They still appear on the ribbon and in counts, but:
    //   * don't contribute a placeholder option to the status filter dropdown
    //     (empty values are filtered out in useGetStatusFilterOptions), and
    //   * never match a `StatusLabelled = X` token, so an admin filtering out
    //     (say) Retired risks can't accidentally hide these items.
    const NO_STATUS = '';

    const approvals: RibbonItem[] = (data.change_request ?? [])
      .filter((cr) =>
        isActiveApprover(
          cr,
          cr.currentUserOwnerList?.map((u) => u.UserId ?? '')
        )
      )
      .map((cr) => ({
        Id: cr.Id,
        Type: types.request,
        StatusLabelled: NO_STATUS,
      }));

    const attestations: RibbonItem[] = (data.attestation_record ?? []).map(
      (d) => ({
        Id: d.Id,
        Type: types.attestation,
        StatusLabelled: NO_STATUS,
      })
    );

    const risks: RibbonItem[] = (data.risk ?? []).map((d) => ({
      Id: d.Id,
      Type: types.risk,
      StatusLabelled:
        getCommonLookupByValue('statuses', d.Status ?? undefined)?.label ??
        t('active'),
    }));

    const actions: RibbonItem[] = (data.action ?? []).map((d) => ({
      Id: d.Id,
      Type: types.action,
      // Mirror the Action Register's label derivation (pages/actions/useLabelledFields.tsx):
      // Open/Pending + DateDue in the past → "Overdue", otherwise the raw status label.
      // Keeping the two in lockstep means ribbon filters and destination-register filters
      // agree on which rows a given StatusLabelled value matches.
      StatusLabelled: isActionStatusOverdue({
        item: { Status: d.Status, DateDue: d.DateDue },
      })
        ? (actionRatings.getByValue('overdue')?.label ??
          actionRatings.getLabel('overdue'))
        : actionRatings.getLabel(d.Status),
    }));

    const assessments: RibbonItem[] = (data.assessment ?? []).map((d) => ({
      Id: d.Id,
      Type: types.assessment,
      StatusLabelled: assessmentRatings.getLabel(
        d.Status ?? Assessment_Status_Enum.Notstarted
      ),
    }));

    const controls: RibbonItem[] = (data.control ?? []).map((d) => ({
      Id: d.Id,
      Type: types.control,
      StatusLabelled: NO_STATUS,
    }));

    const issues: RibbonItem[] = (data.issue ?? []).map((d) => ({
      Id: d.Id,
      Type: types.issue,
      StatusLabelled: issueRatings.getLabel(
        d.assessment?.Status ?? Issue_Assessment_Status_Enum.Open
      ),
    }));

    const activities: RibbonItem[] = (data.assessment_activity ?? []).map(
      (d) => ({
        Id: d.Id,
        Type: types.RCSAActivity,
        StatusLabelled: activityRatings.getLabel(
          d.Status ?? Assessment_Activity_Status_Enum.Inprogress
        ),
      })
    );

    const documents: RibbonItem[] = (data.document ?? []).map((d) => ({
      Id: d.Id,
      Type: types.document,
      StatusLabelled: NO_STATUS,
    }));

    const indicators: RibbonItem[] = (data.indicator ?? []).map((d) => ({
      Id: d.Id,
      Type: types.indicator,
      StatusLabelled: NO_STATUS,
    }));

    const obligations: RibbonItem[] = (data.obligation ?? []).map((d) => ({
      Id: d.Id,
      Type: types.obligationReview,
      StatusLabelled: NO_STATUS,
    }));

    return [
      ...approvals,
      ...attestations,
      ...risks,
      ...actions,
      ...assessments,
      ...controls,
      ...issues,
      ...activities,
      ...documents,
      ...indicators,
      ...obligations,
    ];
  }, [
    data,
    t,
    types,
    isActiveApprover,
    getCommonLookupByValue,
    actionRatings,
    assessmentRatings,
    activityRatings,
    issueRatings,
  ]);
};
